<template>
    <div class="cache-manager-page">
        <div class="header">
            <h2>
                <i class="material-icons">storage</i>
                {{ $t("cache.management") }}
            </h2>
            <div class="header-actions">
                <SupsisButton primary @click="showCreateCacheModal = true">
                    <i class="material-icons">add</i>
                    {{ $t("cache.createNew") }}
                </SupsisButton>
            </div>
        </div>

        <!-- Filters -->
        <div class="filters-section">
            <div class="filter-row">
                <a-input-search
                    v-model="filters.keyPattern"
                    :placeholder="$t('cache.keySearchPlaceholder')"
                    style="width: 300px"
                    @search="fetchCaches"
                />

                <CustomPicker
                    v-model="filters.type"
                    :items="typeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('cache.type')"
                    @input="fetchCaches"
                />

                <CustomPicker
                    v-model="filters.actorType"
                    :items="actorTypeOptions"
                    idKey="value"
                    labelKey="label"
                    :placeholder="$t('cache.actorType')"
                    @input="onActorTypeChange"
                />

                <CustomPicker
                    v-if="filters.actorType === 'automation'"
                    v-model="filters.actorId"
                    :items="automationOptions"
                    idKey="_id"
                    labelKey="name"
                    :placeholder="$t('cache.selectAutomation')"
                    @input="fetchCaches"
                />

                <CustomPicker
                    v-if="filters.actorType === 'chatbot'"
                    v-model="filters.actorId"
                    :items="chatbotOptions"
                    idKey="_id"
                    labelKey="title"
                    :placeholder="$t('cache.selectChatbot')"
                    @input="fetchCaches"
                />

                <CustomPicker
                    v-if="filters.actorType === 'user'"
                    v-model="filters.actorId"
                    :items="userOptions"
                    idKey="_id"
                    labelKey="name"
                    :placeholder="$t('cache.selectUser')"
                    @input="fetchCaches"
                />

                <SupsisButton ghost @click="clearFilters">
                    <i class="material-icons">clear</i>
                    {{ $t("cache.clearFilters") }}
                </SupsisButton>
            </div>

            <div class="filter-row" v-if="selectionMode">
                <div class="selection-info">
                    <span>{{ $t("cache.selectedCount", { count: selectedCaches.length }) }}</span>
                    <SupsisButton ghost @click="exitSelectionMode">{{ $t("common.cancel") }}</SupsisButton>
                </div>
                <div class="bulk-actions">
                    <SupsisButton danger @click="bulkDeleteCaches">
                        <i class="material-icons">delete</i>
                        {{ $t("cache.deleteSelected") }}
                    </SupsisButton>
                    <SupsisButton @click="showBulkExtendModal = true">
                        <i class="material-icons">schedule</i>
                        {{ $t("cache.extendSelected") }}
                    </SupsisButton>
                    <SupsisButton @click="bulkMakeNeverExpire">
                        <i class="material-icons">all_inclusive</i>
                        {{ $t("cache.makeSelectedNeverExpire") }}
                    </SupsisButton>
                </div>
            </div>

            <div class="filter-row" v-if="filters.actorType && filters.actorId">
                <SupsisButton danger @click="deleteAllActorCaches">
                    <i class="material-icons">delete_sweep</i>
                    {{ $t("cache.deleteAllActorCaches") }}
                </SupsisButton>
            </div>
        </div>

        <!-- Table -->
        <div class="table-wrapper">
            <a-table
                :columns="columns"
                :data-source="caches"
                :pagination="tablePagination"
                :loading="loading"
                :row-key="(record) => record._id"
                :row-selection="rowSelection"
                :row-class-name="getRowClassName"
                :custom-row="customRow"
                @change="handleTableChange"
                :scroll="{ x: 1500 }"
                size="small"
                :locale="{
                    emptyText: $t('cache.emptyState'),
                }"
            >
                <template #empty>
                    <div class="empty-state">
                        <i class="material-icons">inbox</i>
                        <p>{{ $t("cache.emptyState") }}</p>
                    </div>
                </template>
            </a-table>
        </div>

        <!-- Create/Edit Modal -->
        <Modal2
            v-if="showCreateCacheModal || showEditCacheModal"
            :title="editingCache ? $t('cache.editCache') : $t('cache.createNew')"
            show-title
            :show-footer="false"
            @cancel="closeCacheModal"
        >
            <div class="cache-form">
                <div class="form-group">
                    <label>{{ $t("cache.key") }}</label>
                    <a-input
                        v-model="cacheForm.key"
                        :placeholder="$t('cache.keyPlaceholder')"
                        :disabled="!!editingCache"
                    />
                </div>

                <div class="form-group">
                    <label>{{ $t("cache.value") }}</label>
                    <template v-if="cacheForm.dataType === 'string'">
                        <a-input v-model="cacheForm.value" :placeholder="$t('cache.stringValuePlaceholder')" />
                    </template>
                    <template v-else-if="cacheForm.dataType === 'number'">
                        <a-input-number v-model="cacheForm.value" style="width: 100%" />
                    </template>
                    <template v-else-if="cacheForm.dataType === 'boolean'">
                        <a-switch v-model="cacheForm.value" />
                    </template>
                    <template v-else-if="cacheForm.dataType === 'date'">
                        <a-date-picker v-model="cacheForm.value" style="width: 100%" show-time />
                    </template>
                    <template v-else-if="cacheForm.dataType === 'objectid_user'">
                        <CustomPicker
                            v-model="cacheForm.value"
                            :items="userOptions"
                            idKey="_id"
                            labelKey="name"
                            :placeholder="$t('cache.selectUser')"
                        />
                    </template>
                    <template v-else>
                        <v-jsoneditor
                            v-model="cacheFormValueObject"
                            :options="jsonEditorOptions"
                            height="300px"
                        ></v-jsoneditor>
                    </template>
                </div>

                <div class="form-group">
                    <label>{{ $t("cache.dataType") }}</label>
                    <CustomPicker
                        v-model="cacheForm.dataType"
                        :items="dataTypeOptions"
                        idKey="value"
                        labelKey="label"
                    />
                </div>

                <div class="form-group">
                    <label>{{ $t("cache.type") }}</label>
                    <CustomPicker
                        v-model="cacheForm.type"
                        :items="typeOptions"
                        idKey="value"
                        labelKey="label"
                    />
                </div>

                <div class="form-group">
                    <label>{{ $t("cache.validityDuration") }}</label>
                    <div class="expires-config">
                        <a-radio-group v-model="expiresMode">
                            <a-radio value="never">{{ $t("cache.neverExpire") }}</a-radio>
                            <a-radio value="duration">{{ $t("cache.specifyDuration") }}</a-radio>
                            <a-radio value="date">{{ $t("cache.specifyDate") }}</a-radio>
                        </a-radio-group>

                        <div v-if="expiresMode === 'duration'" class="duration-input">
                            <a-input-number v-model="expiresDuration.value" :min="1" />
                            <a-select v-model="expiresDuration.unit" style="width: 120px">
                                <a-select-option value="minutes">{{ $t("cache.minutes") }}</a-select-option>
                                <a-select-option value="hours">{{ $t("cache.hours") }}</a-select-option>
                                <a-select-option value="days">{{ $t("cache.days") }}</a-select-option>
                            </a-select>
                        </div>

                        <div v-if="expiresMode === 'date'">
                            <a-date-picker v-model="cacheForm.expiresAt" show-time style="width: 100%" />
                        </div>
                    </div>
                </div>

                <div class="form-actions">
                    <SupsisButton @click="closeCacheModal">{{ $t("common.cancel") }}</SupsisButton>
                    <SupsisButton primary @click="saveCache" :loading="saving">
                        {{ editingCache ? $t("cache.update") : $t("cache.create") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>

        <!-- View Value Modal -->
        <Modal2
            v-if="showValueModal"
            :title="$t('cache.valueTitle', { key: $_.get(viewingCache, 'key', '') })"
            show-title
            :show-footer="false"
            @cancel="showValueModal = false"
            class-name="value-viewer-modal"
        >
            <div class="value-viewer">
                <div class="value-meta">
                    <span><strong>Type:</strong> {{ $_.get(viewingCache, 'type') }}</span>
                    <span><strong>Data Type:</strong> {{ $_.get(viewingCache, 'dataType') }}</span>
                </div>
                <div class="value-content">
                    <v-jsoneditor
                        v-if="['json', 'array', 'mixed'].includes($_.get(viewingCache, 'dataType'))"
                        v-model="viewingCacheValue"
                        :options="jsonEditorViewOptions"
                        height="500px"
                    ></v-jsoneditor>
                    <div v-else class="simple-value">{{ $_.get(viewingCache, 'value') }}</div>
                </div>
            </div>
        </Modal2>

        <!-- Bulk Extend Modal -->
        <Modal2
            v-if="showBulkExtendModal"
            :title="$t('cache.bulkExtend')"
            show-title
            :show-footer="false"
            @cancel="showBulkExtendModal = false"
        >
            <div class="bulk-extend-form">
                <p>{{ $t("cache.bulkExtendInfo", { count: selectedCaches.length }) }}</p>
                <div class="form-group">
                    <label>{{ $t("cache.newValidityDuration") }}</label>
                    <div class="duration-input">
                        <a-input-number v-model="bulkExpiresDuration.value" :min="1" />
                        <a-select v-model="bulkExpiresDuration.unit" style="width: 120px">
                            <a-select-option value="minutes">{{ $t("cache.minutes") }}</a-select-option>
                            <a-select-option value="hours">{{ $t("cache.hours") }}</a-select-option>
                            <a-select-option value="days">{{ $t("cache.days") }}</a-select-option>
                        </a-select>
                    </div>
                </div>
                <div class="form-actions">
                    <SupsisButton @click="showBulkExtendModal = false">{{ $t("common.cancel") }}</SupsisButton>
                    <SupsisButton primary @click="bulkExtendCaches" :loading="saving">
                        {{ $t("cache.extend") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { CACHE_API } from "../../../api/cache";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import Modal2 from "../../../components/common/Modal2.vue";
import CustomPicker from "../../../components/pickers/CustomPicker.vue";
import Avatar from "vue-avatar";
import { mapState } from "vuex";
import VJsoneditor from "v-jsoneditor";
import AutomationIcon from "../../../components/svg/appSwitcherIcon/AutomationIcon.vue";

export default {
    name: "CacheManager",
    components: {
        SupsisButton,
        Modal2,
        CustomPicker,
        Avatar,
        VJsoneditor,
        AutomationIcon,
    },
    props: {
        defaultFilters: {
            type: Object,
            default: () => ({}),
        },
        modalMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            caches: [],
            loading: false,
            filters: {
                keyPattern: null,
                type: null,
                actorType: null,
                actorId: null,
                includeExpired: false,
                ...this.defaultFilters,
            },
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
            },
            typeOptionsData: [
                { value: null, labelKey: "cache.all" },
                { value: "public", labelKey: "cache.public" },
                { value: "private", labelKey: "cache.private" },
            ],
            actorTypeOptionsData: [
                { value: null, labelKey: "cache.all" },
                { value: "automation", labelKey: "cache.automation" },
                { value: "chatbot", labelKey: "cache.chatbot" },
                { value: "user", labelKey: "cache.user" },
                { value: "api", labelKey: "cache.api" },
            ],
            dataTypeOptions: [
                { value: "string", label: "String" },
                { value: "number", label: "Number" },
                { value: "boolean", label: "Boolean" },
                { value: "date", label: "Date" },
                { value: "json", label: "JSON" },
                { value: "array", label: "Array" },
                { value: "mixed", label: "Mixed" },
                { value: "objectid_user", label: "User ID" },
            ],
            showCreateCacheModal: false,
            showEditCacheModal: false,
            showValueModal: false,
            showBulkExtendModal: false,
            editingCache: null,
            viewingCache: null,
            cacheForm: {
                key: "",
                value: "",
                type: "public",
                dataType: "string",
                expiresAt: null,
            },
            expiresMode: "never",
            expiresDuration: {
                value: 24,
                unit: "hours",
            },
            bulkExpiresDuration: {
                value: 24,
                unit: "hours",
            },
            saving: false,
            selectionMode: false,
            selectedCaches: [],
            jsonEditorOptions: {
                mode: "code",
                modes: ["tree", "code", "form"],
                navigationBar: true,
                mainMenuBar: true,
                statusBar: true,
                search: true,
            },
            jsonEditorViewOptions: {
                mode: "tree",
                modes: ["tree", "code", "view"],
                navigationBar: true,
                mainMenuBar: true,
                statusBar: true,
                search: true,
                expandAll: false,
            },
        };
    },
    computed: {
        ...mapState({
            automations: (state) => state.automation.automations || [],
            chatbots: (state) => state.CB.stories || [],
            users: (state) => state.user.users?.data || [],
        }),
        typeOptions() {
            return this.typeOptionsData.map((opt) => ({
                value: opt.value,
                label: this.$t(opt.labelKey),
            }));
        },
        actorTypeOptions() {
            return this.actorTypeOptionsData.map((opt) => ({
                value: opt.value,
                label: this.$t(opt.labelKey),
            }));
        },
        automationOptions() {
            return this.automations.filter((a) => !a.isDeleted);
        },
        chatbotOptions() {
            return this.chatbots;
        },
        userOptions() {
            return this.users.map((u) => ({ ...u, name: `${u.name} (${u.email})` }));
        },
        tablePagination() {
            return {
                current: this.pagination.page,
                pageSize: this.pagination.perPage,
                total: this.pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            };
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedCaches,
                onChange: (selectedRowKeys, selectedRows) => {
                    this.selectedCaches = selectedRowKeys;
                    // İlk seçimde selection mode'u aç
                    if (selectedRowKeys.length > 0 && !this.selectionMode) {
                        this.selectionMode = true;
                    }
                },
                onSelect: (record, selected, selectedRows) => {
                    // Tek bir checkbox tıklandığında
                    if (!this.selectionMode) {
                        this.selectionMode = true;
                    }
                },
                onSelectAll: (selected, selectedRows, changeRows) => {
                    // Tümünü seç/bırak tıklandığında
                    if (!this.selectionMode && selected) {
                        this.selectionMode = true;
                    }
                },
                getCheckboxProps: (record) => ({
                    props: {
                        name: record._id,
                    },
                }),
            };
        },
        cacheFormValueObject: {
            get() {
                if (["json", "array", "mixed"].includes(this.cacheForm.dataType)) {
                    if (typeof this.cacheForm.value === "string") {
                        try {
                            return JSON.parse(this.cacheForm.value);
                        } catch (e) {
                            return {};
                        }
                    }
                    return this.cacheForm.value;
                }
                return {};
            },
            set(val) {
                this.cacheForm.value = val;
            },
        },
        viewingCacheValue: {
            get() {
                const value = this.$_.get(this.viewingCache, "value");
                if (typeof value === "string") {
                    try {
                        return JSON.parse(value);
                    } catch (e) {
                        return value;
                    }
                }
                return value;
            },
            set(val) {
                // Read-only, değişiklik yapılmaz
            },
        },
        columns() {
            const cols = [];

            cols.push(
                {
                    dataIndex: "key",
                    key: "key",
                    title: this.$t("cache.keyLabel"),
                    width: 250,
                    ellipsis: true,
                    customRender: (text, record) => {
                        return this.$createElement("div", { class: "cache-key" }, [
                            this.$createElement("code", { attrs: { title: text } }, text),
                        ]);
                    },
                },
                {
                    dataIndex: "value",
                    key: "value",
                    title: this.$t("cache.valueLabel"),
                    width: 300,
                    ellipsis: {
                        showTitle: true,
                    },
                    customRender: (text, record) => {
                        const valueStr = typeof record.value === "string" ? record.value : JSON.stringify(record.value);
                        const truncated = this.truncateValue(record.value);
                        return this.$createElement(
                            "div",
                            {
                                class: "cache-value",
                                attrs: {
                                    title: valueStr,
                                },
                                on: {
                                    click: () => this.viewValue(record),
                                },
                            },
                            [
                                this.$createElement("span", { class: "value-text" }, truncated),
                                this.$createElement("i", { class: "material-icons view-icon" }, "visibility"),
                            ]
                        );
                    },
                },
                {
                    dataIndex: "type",
                    key: "type",
                    title: this.$t("cache.typeLabel"),
                    width: 100,
                    ellipsis: true,
                    customRender: (text, record) => {
                        const color = record.type === "public" ? "#52c41a" : "#fa8c16";
                        return this.$createElement(
                            "span",
                            {
                                class: "cache-badge",
                                style: { backgroundColor: color },
                            },
                            record.type
                        );
                    },
                },
                {
                    dataIndex: "dataType",
                    key: "dataType",
                    title: this.$t("cache.dataType"),
                    width: 120,
                    ellipsis: true,
                    customRender: (text, record) => {
                        return this.$createElement("span", { class: "data-type-badge" }, record.dataType);
                    },
                },
                {
                    dataIndex: "createdBy",
                    key: "createdBy",
                    title: this.$t("cache.createdBy"),
                    width: 200,
                    ellipsis: true,
                    customRender: (text, record) => {
                        return this.renderActorCell(record, "created");
                    },
                },
                {
                    dataIndex: "createdAt",
                    key: "createdAt",
                    title: this.$t("cache.createdAt"),
                    width: 150,
                    ellipsis: true,
                    customRender: (text, record) => {
                        return this.$createElement("span", {
                            class: "date-text"
                        }, this.$moment(record.createdAt).format("DD.MM.YYYY HH:mm"));
                    },
                },
                {
                    dataIndex: "updatedBy",
                    key: "updatedBy",
                    title: this.$t("cache.updatedBy"),
                    width: 200,
                    ellipsis: true,
                    customRender: (text, record) => {
                        return this.renderActorCell(record, "updated");
                    },
                },
                {
                    dataIndex: "expiresAt",
                    key: "expiresAt",
                    title: this.$t("cache.expiresAt"),
                    width: 150,
                    ellipsis: true,
                    customRender: (text, record) => {
                        if (!record.expiresAt) {
                            return this.$createElement("span", { class: "never-expires" }, this.$t("cache.never"));
                        }
                        const isExpired = moment(record.expiresAt).isBefore(moment());
                        const timeText = moment(record.expiresAt).fromNow();
                        return this.$createElement(
                            "span",
                            {
                                class: isExpired ? "expired-badge" : "expires-badge",
                            },
                            timeText
                        );
                    },
                },
                {
                    dataIndex: "actions",
                    key: "actions",
                    title: this.$t("cache.actions"),
                    width: 200,
                    fixed: "right",
                    customRender: (text, record) => {
                        return this.$createElement("div", { class: "action-buttons" }, [
                            this.$createElement(
                                "a-tooltip",
                                {
                                    props: { title: this.$t("cache.edit") },
                                },
                                [
                                    this.$createElement("a-button", {
                                        props: { size: "small", icon: "edit" },
                                        on: { click: (e) => {
                                            e.stopPropagation();
                                            this.editCache(record);
                                        }},
                                    }),
                                ]
                            ),
                            this.$createElement(
                                "a-tooltip",
                                {
                                    props: { title: this.$t("cache.extendDuration") },
                                },
                                [
                                    this.$createElement("a-button", {
                                        props: { size: "small", icon: "schedule" },
                                        on: { click: (e) => {
                                            e.stopPropagation();
                                            this.quickExtendCache(record);
                                        }},
                                    }),
                                ]
                            ),
                            this.$createElement(
                                "a-tooltip",
                                {
                                    props: { title: this.$t("cache.makeNeverExpire") },
                                },
                                [
                                    this.$createElement("a-button", {
                                        props: { size: "small", disabled: !record.expiresAt },
                                        on: { click: (e) => {
                                            e.stopPropagation();
                                            this.makeNeverExpire(record);
                                        }},
                                    }, [
                                        this.$createElement("i", { class: "material-icons", style: "font-size: 14px" }, "all_inclusive")
                                    ]),
                                ]
                            ),
                            this.$createElement(
                                "a-tooltip",
                                {
                                    props: { title: this.$t("common.delete") },
                                },
                                [
                                    this.$createElement("a-button", {
                                        props: { size: "small", icon: "delete", type: "danger" },
                                        on: { click: (e) => {
                                            e.stopPropagation();
                                            this.enterSelectionModeWithCache(record);
                                        }},
                                    }),
                                ]
                            ),
                        ]);
                    },
                }
            );

            return cols;
        },
    },
    async mounted() {
        await this.fetchCaches();
    },
    methods: {
        async fetchCaches() {
            try {
                this.loading = true;
                const result = await CACHE_API.listCaches(
                    {
                        keyPattern: this.filters.keyPattern || null,
                        type: this.filters.type,
                        actorType: this.filters.actorType,
                        actorId: this.filters.actorId,
                        includeExpired: this.filters.includeExpired,
                    },
                    {
                        page: this.pagination.page,
                        perPage: this.pagination.perPage,
                    }
                );

                this.caches = result.items;
                this.pagination.total = result.total;
            } catch (error) {
                console.error("Error fetching caches:", error);
                this.$message.error(this.$t("cache.loadError"));
            } finally {
                this.loading = false;
            }
        },

        onPaginationChange(cursor) {
            this.pagination = cursor;
            this.fetchCaches();
        },

        handleTableChange(pagination, filters, sorter) {
            this.pagination.page = pagination.current;
            this.pagination.perPage = pagination.pageSize;
            this.fetchCaches();
        },

        customRow(record) {
            return {
                on: {
                    click: (event) => {
                        // Checkbox veya action butonlarına tıklanmışsa ignore et
                        const target = event.target;
                        const isCheckbox = target.closest('.ant-checkbox-wrapper') || target.closest('.ant-table-selection-column');
                        const isActionButton = target.closest('.action-buttons');
                        
                        if (!isCheckbox && !isActionButton && this.selectionMode) {
                            this.toggleCacheSelection(record._id);
                        }
                    },
                },
            };
        },

        onActorTypeChange() {
            this.filters.actorId = null;
            this.fetchCaches();
        },

        clearFilters() {
            this.filters = {
                keyPattern: null,
                type: null,
                actorType: null,
                actorId: null,
                includeExpired: false,
                ...this.defaultFilters,
            };
            this.pagination.page = 1;
            this.fetchCaches();
        },

        truncateValue(value) {
            const str = typeof value === "string" ? value : JSON.stringify(value);
            return str.length > 50 ? str.substring(0, 50) + "..." : str;
        },

        formatJSON(value) {
            return JSON.stringify(value, null, 2);
        },

        viewValue(cache) {
            this.viewingCache = cache;
            this.showValueModal = true;
        },

        editCache(cache) {
            this.editingCache = cache;
            this.cacheForm = {
                key: cache.key,
                value: cache.value,
                type: cache.type,
                dataType: cache.dataType,
                expiresAt: cache.expiresAt ? moment(cache.expiresAt) : null,
            };

            if (cache.expiresAt) {
                this.expiresMode = "date";
            } else {
                this.expiresMode = "never";
            }

            this.showEditCacheModal = true;
        },

        closeCacheModal() {
            this.showCreateCacheModal = false;
            this.showEditCacheModal = false;
            this.editingCache = null;
            this.cacheForm = {
                key: "",
                value: "",
                type: "public",
                dataType: "string",
                expiresAt: null,
            };
            this.expiresMode = "never";
        },

        async saveCache() {
            try {
                this.saving = true;

                let expiresAt = null;
                if (this.expiresMode === "duration") {
                    expiresAt = moment()
                        .add(this.expiresDuration.value, this.expiresDuration.unit)
                        .toDate();
                } else if (this.expiresMode === "date" && this.cacheForm.expiresAt) {
                    expiresAt = this.cacheForm.expiresAt.toDate
                        ? this.cacheForm.expiresAt.toDate()
                        : this.cacheForm.expiresAt;
                }

                if (this.editingCache) {
                    // Update
                    await CACHE_API.updateCacheValue(
                        this.editingCache._id,
                        this.cacheForm.value,
                        this.cacheForm.dataType
                    );

                    if (expiresAt) {
                        await CACHE_API.extendCache(this.editingCache._id, expiresAt);
                    }

                    this.$message.success(this.$t("cache.updateSuccess"));
                } else {
                    // Create
                    await CACHE_API.writeCache(this.cacheForm.key, this.cacheForm.value, {
                        type: this.cacheForm.type,
                        dataType: this.cacheForm.dataType,
                        expiresAt,
                    });

                    this.$message.success(this.$t("cache.createSuccess"));
                }

                this.closeCacheModal();
                await this.fetchCaches();
            } catch (error) {
                console.error("Error saving cache:", error);
                this.$message.error(this.$t("cache.saveError"));
            } finally {
                this.saving = false;
            }
        },

        async deleteCache(cache) {
            try {
                await this.$confirm({
                    title: this.$t("cache.deleteCache"),
                    content: this.$t("cache.deleteCacheConfirm", { key: cache.key }),
                    okText: this.$t("cache.confirmDelete"),
                    okType: "danger",
                    cancelText: this.$t("common.cancel"),
                    onOk: async () => {
                        await CACHE_API.deleteCache(cache._id);
                        this.$message.success(this.$t("cache.deleteSuccess"));
                        await this.fetchCaches();
                    },
                });
            } catch (error) {
                // User cancelled
            }
        },

        async quickExtendCache(cache) {
            try {
                const newExpiresAt = moment().add(24, "hours").toDate();
                await CACHE_API.extendCache(cache._id, newExpiresAt);
                this.$message.success(this.$t("cache.extendSuccess24h"));
                await this.fetchCaches();
            } catch (error) {
                console.error("Error extending cache:", error);
                this.$message.error(this.$t("cache.extendError"));
            }
        },

        async deleteAllActorCaches() {
            try {
                const actorName = this.getActorName(this.filters.actorType, this.filters.actorId);

                await this.$confirm({
                    title: this.$t("cache.deleteActorCaches"),
                    content: this.$t("cache.deleteActorCachesConfirm", { actor: actorName }),
                    okText: this.$t("cache.confirmDeleteAll"),
                    okType: "danger",
                    cancelText: this.$t("common.cancel"),
                    onOk: async () => {
                        const result = await CACHE_API.deleteCachesByActor(
                            this.filters.actorType,
                            this.filters.actorId
                        );
                        this.$message.success(
                            this.$t("cache.countDeleted", { count: result.deletedCount })
                        );
                        await this.fetchCaches();
                    },
                });
            } catch (error) {
                // User cancelled
            }
        },

        getActorName(actorType, actorId) {
            if (actorType === "automation") {
                const auto = this.automations.find((a) => a._id === actorId);
                return auto ? auto.name : actorId;
            } else if (actorType === "chatbot") {
                const cb = this.chatbots.find((c) => c._id === actorId);
                return cb ? cb.title : actorId;
            } else if (actorType === "user") {
                const user = this.users.find((u) => u._id === actorId);
                return user ? user.name : actorId;
            }
            return actorType;
        },

        renderActorCell(cache, prefix) {
            const actorType = cache[`${prefix}ByActorType`];
            const actorAutomation = cache[`${prefix}ByAutomation`];
            const actorChatbot = cache[`${prefix}ByChatbot`];
            const actorUser = cache[`${prefix}ByUser`];

            if (actorType === "automation" && actorAutomation) {
                const auto = this.automations.find((a) => a._id === actorAutomation);
                const href = auto ? `#/automations/${auto._id}/design` : null;
                
                return this.$createElement("div", { class: "actor-cell" }, [
                    this.$createElement(AutomationIcon, { 
                        class: "actor-icon automation-icon",
                        style: { width: "20px", height: "20px" }
                    }),
                    href 
                        ? this.$createElement("a", {
                            attrs: { href },
                            class: "actor-link",
                            on: {
                                click: (e) => {
                                    if (!e.ctrlKey && !e.metaKey) {
                                        e.preventDefault();
                                        this.$router.push(`/automations/${auto._id}/design`);
                                    }
                                }
                            }
                        }, auto.name)
                        : this.$createElement("span", this.$t("cache.automation")),
                ]);
            } else if (actorType === "chatbot" && actorChatbot) {
                const cb = this.chatbots.find((c) => c._id === actorChatbot);
                const version = cb ? this.$_.get(cb.meta, 'versions', []).find(v => v.isDraft)?.version || '1.0' : '1.0';
                const href = cb ? `#/chatbot/story/${cb._id}/${version}` : null;
                
                return this.$createElement("div", { class: "actor-cell" }, [
                    this.$createElement("i", { class: "material-icons", style: { color: "#52c41a" } }, "smart_toy"),
                    href 
                        ? this.$createElement("a", {
                            attrs: { href },
                            class: "actor-link",
                            on: {
                                click: (e) => {
                                    if (!e.ctrlKey && !e.metaKey) {
                                        e.preventDefault();
                                        this.$router.push(`/chatbot/story/${cb._id}/${version}`);
                                    }
                                }
                            }
                        }, cb.title)
                        : this.$createElement("span", this.$t("cache.chatbot")),
                ]);
            } else if (actorType === "user" && actorUser) {
                const user = this.users.find((u) => u._id === actorUser);
                return this.$createElement("div", { class: "actor-cell" }, [
                    this.$createElement(Avatar, {
                        props: {
                            size: 24,
                            src: user ? user.gravatar : null,
                            username: user ? user.name : "User",
                        },
                    }),
                    this.$createElement("span", user ? user.name : "User"),
                ]);
            } else if (actorType === "api") {
                return this.$createElement("div", { class: "actor-cell" }, [
                    this.$createElement("i", { class: "material-icons", style: { color: "#722ed1" } }, "api"),
                    this.$createElement("span", "API"),
                ]);
            }

            return this.$createElement("span", "-");
        },

        toggleCacheSelection(cacheId) {
            const index = this.selectedCaches.indexOf(cacheId);
            if (index > -1) {
                this.selectedCaches.splice(index, 1);
            } else {
                this.selectedCaches.push(cacheId);
            }

            if (this.selectedCaches.length === 0) {
                this.selectionMode = false;
            }
        },

        enterSelectionMode(cache) {
            this.selectionMode = true;
            this.selectedCaches = [cache._id];
        },

        exitSelectionMode() {
            this.selectionMode = false;
            this.selectedCaches = [];
        },

        async bulkDeleteCaches() {
            try {
                await this.$confirm({
                    title: this.$t("cache.bulkDelete"),
                    content: this.$t("cache.bulkDeleteConfirm", { count: this.selectedCaches.length }),
                    okText: this.$t("cache.confirmDelete"),
                    okType: "danger",
                    cancelText: this.$t("common.cancel"),
                    onOk: async () => {
                        const result = await CACHE_API.bulkDeleteCaches(this.selectedCaches);
                        this.$message.success(this.$t("cache.countDeleted", { count: result.deletedCount }));
                        this.exitSelectionMode();
                        await this.fetchCaches();
                    },
                });
            } catch (error) {
                // User cancelled
            }
        },

        async bulkExtendCaches() {
            try {
                this.saving = true;
                const newExpiresAt = moment()
                    .add(this.bulkExpiresDuration.value, this.bulkExpiresDuration.unit)
                    .toDate();

                for (const cacheId of this.selectedCaches) {
                    await CACHE_API.extendCache(cacheId, newExpiresAt);
                }

                this.$message.success(this.$t("cache.bulkExtendSuccess", { count: this.selectedCaches.length }));
                this.showBulkExtendModal = false;
                this.exitSelectionMode();
                await this.fetchCaches();
            } catch (error) {
                console.error("Error bulk extending caches:", error);
                this.$message.error(this.$t("cache.bulkExtendError"));
            } finally {
                this.saving = false;
            }
        },

        async makeNeverExpire(cache) {
            try {
                await CACHE_API.makeNeverExpire(cache._id);
                this.$message.success(this.$t("cache.makeNeverExpireSuccess"));
                await this.fetchCaches();
            } catch (error) {
                console.error("Error making cache never expire:", error);
                this.$message.error(this.$t("cache.makeNeverExpireError"));
            }
        },

        toggleSelectAll() {
            if (this.selectedCaches.length === this.caches.length) {
                this.selectedCaches = [];
                this.exitSelectionMode();
            } else {
                this.selectedCaches = this.caches.map(c => c._id);
            }
        },

        enterSelectionModeWithCache(cache) {
            this.selectionMode = true;
            this.selectedCaches = [cache._id];
        },

        async bulkMakeNeverExpire() {
            try {
                await this.$confirm({
                    title: this.$t("cache.bulkMakeNeverExpire"),
                    content: this.$t("cache.bulkMakeNeverExpireConfirm", { count: this.selectedCaches.length }),
                    okText: this.$t("common.yes"),
                    okType: "primary",
                    cancelText: this.$t("common.cancel"),
                    onOk: async () => {
                        this.saving = true;
                        for (const cacheId of this.selectedCaches) {
                            await CACHE_API.makeNeverExpire(cacheId);
                        }
                        this.$message.success(this.$t("cache.bulkMakeNeverExpireSuccess", { count: this.selectedCaches.length }));
                        this.exitSelectionMode();
                        await this.fetchCaches();
                        this.saving = false;
                    },
                });
            } catch (error) {
                // User cancelled
            }
        },

        getRowClassName(record, index) {
            if (this.selectionMode && this.selectedCaches.includes(record._id)) {
                return "selected-row";
            }
            return "";
        },
    },
};
</script>

<style lang="scss" scoped>
.cache-manager-page {
    padding: 20px;
    background: #fff;
    min-height: calc(100vh - 60px);

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid #e8e8e8;

        h2 {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0;
            font-size: 24px;

            .material-icons {
                font-size: 28px;
                color: #1890ff;
            }
        }
    }

    .filters-section {
        background: #fafafa;
        padding: 15px;
        border-radius: 8px;
        margin-bottom: 20px;

        .filter-row {
            display: flex;
            gap: 10px;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 10px;

            &:last-child {
                margin-bottom: 0;
            }

            .selection-info {
                display: flex;
                align-items: center;
                gap: 15px;
                padding: 10px 15px;
                background: #e6f7ff;
                border-radius: 6px;
                color: #1890ff;
                font-weight: 500;
            }

            .bulk-actions {
                display: flex;
                gap: 10px;
            }
        }
    }

    .table-wrapper {
        background: #fff;
        border-radius: 8px;
        overflow: auto;

        .ant-table {
            font-size: 13px;

            .ant-table-thead > tr > th,
            .ant-table-tbody > tr > td {
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .ant-table-tbody > tr {
                cursor: pointer;
                transition: background-color 0.2s;

                &.selected-row {
                    background-color: #e6f7ff !important;
                }

                &:hover {
                    background-color: #f5f5f5;
                }

                &.selected-row:hover {
                    background-color: #bae7ff !important;
                }
            }

            .ant-table-fixed-right {
                box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
            }
        }
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
        color: #999;

        .material-icons {
            font-size: 64px;
            margin-bottom: 16px;
        }

        p {
            font-size: 16px;
            margin: 0;
        }
    }
}

.cache-form,
.bulk-extend-form {
    padding: 20px;

    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #333;
        }

        .expires-config {
            display: flex;
            flex-direction: column;
            gap: 15px;

            .duration-input {
                display: flex;
                gap: 10px;
            }
        }
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 30px;
    }
}

.value-viewer {
    padding: 20px;

    .value-meta {
        display: flex;
        gap: 20px;
        margin-bottom: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid #e8e8e8;

        span {
            font-size: 14px;

            strong {
                color: #1890ff;
            }
        }
    }

    .value-content {
        .jsoneditor-container {
            min-height: 500px;
            border: 1px solid #e8e8e8;
            border-radius: 6px;
        }

        .simple-value {
            padding: 15px;
            background: #f5f5f5;
            border-radius: 6px;
            font-size: 14px;
            line-height: 1.5;
            word-break: break-word;
        }
    }
}
</style>

<style lang="scss">
.cache-manager-page {
    .cache-key {
        max-width: 100%;
        overflow: hidden;

        code {
            background: #f0f0f0;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            color: #d63384;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .cache-value {
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 100%;
        overflow: hidden;

        .value-text {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &:hover {
            .view-icon {
                opacity: 1;
            }
        }

        .view-icon {
            opacity: 0;
            font-size: 18px;
            color: #1890ff;
            transition: opacity 0.2s;
            flex-shrink: 0;
        }
    }

    .cache-badge {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .data-type-badge {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 4px;
        background: #e6f7ff;
        color: #1890ff;
        font-size: 11px;
        font-weight: 500;
        white-space: nowrap;
    }

    .actor-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 100%;
        overflow: hidden;

        .material-icons {
            font-size: 20px;
            flex-shrink: 0;
        }

        .actor-icon {
            width: 20px;
            height: 20px;
            flex-shrink: 0;

            &.automation-icon {
                color: #1890ff;

                :deep(svg) {
                    fill: currentColor !important;

                    path {
                        fill: currentColor !important;
                    }
                }
            }
        }

        span {
            font-size: 13px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .actor-link {
            color: #1890ff;
            text-decoration: none;
            cursor: pointer;
            font-size: 13px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    .date-text {
        font-size: 12px;
        color: #666;
        white-space: nowrap;
    }

    .never-expires {
        color: #52c41a;
        font-weight: 500;
        white-space: nowrap;
    }

    .expires-badge {
        color: #faad14;
        white-space: nowrap;
    }

    .expired-badge {
        color: #ff4d4f;
        font-weight: 500;
        white-space: nowrap;
    }

    .action-buttons {
        display: flex;
        gap: 5px;
        white-space: nowrap;
    }
}

.value-viewer-modal {
    .ant-modal {
        width: 900px !important;
        max-width: 90vw !important;
    }

    .value-content {
        .jsoneditor-container {
            min-height: 500px;
        }
    }
}

.cache-form {
    .jsoneditor-container {
        min-height: 300px;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
    }
}
</style>


